import React, { useMemo, useState, useEffect } from 'react';
import { getEventDates } from '@/lib/events';
import { Heading } from '@/components';
import { SingleEvent } from '@/types/EventInterface';
import { formatInTimeZone, format } from 'date-fns-tz';
import { ClockIcon, CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { buttonVariants } from './ui/button';
import { cn } from '@/lib/utils';
import { AddToCalendar } from '@/components';
import { CalendarEventInterface } from '@/types';
import { isBefore } from 'date-fns';
import { DateTime } from 'luxon';

interface CalendarWidgetProps extends SingleEvent {
  isExpiredEvent: boolean;
  title: string;
}

/**
 * CalendarWidget Component
 *
 * Displays a calendar with available event dates and their corresponding times.
 * Handles timezone conversion to EST/EDT and displays selected date information.
 *
 * The timezone conversion is centralized here so that the calendar object passed
 * to AddToCalendar already contains correctly adjusted dates.
 *
 * @param {SingleEvent} props - Contains eventDates and exceptions arrays
 * @param {Array} props.eventDates - Array of event dates with frequency information
 * @param {Array} props.exceptions - Array of date exceptions (cancelled or modified dates)
 * @param {boolean} props.isExpiredEvent - Whether the event has expired
 * @param {string} props.title - Title of the event
 */
export function CalendarWidget({
  eventDates = [],
  exceptions = [],
  isExpiredEvent,
  title,
}: CalendarWidgetProps) {
  // State management for selected date information
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [formattedDate, setFormattedDate] = useState<string | null>(null);
  const [formattedTime, setFormattedTime] = useState<string | null>(null);
  const [calendarObject, setCalendarObject] = useState<CalendarEventInterface | null>(null);

  const timeZone = 'America/New_York';

  // Process all event dates using the getEventDates utility
  const allDates = useMemo(
    () => getEventDates([{ eventDates, exceptions }]),
    [eventDates, exceptions],
  );

  // Extract just the dates for the calendar's available dates
  const availableDates = useMemo(() => allDates.map((d) => d.date), [allDates]);

  // Create a Set of cancelled event dates for quick lookup
  const cancelledDatesSet = useMemo(() => {
    return new Set(
      exceptions
        ?.filter((ex) => ex.cancelEvent)
        .map((ex) =>
          formatInTimeZone(new Date(`${ex.exceptionDate}T00:00:00`), timeZone, 'yyyy-MM-dd'),
        ),
    );
  }, [exceptions, timeZone]);

  // Create a Set of available dates for quick lookup
  const availableDatesSet = useMemo(() => {
    return new Set(availableDates.map((date) => formatInTimeZone(date, timeZone, 'yyyy-MM-dd')));
  }, [availableDates, timeZone]);

  // Determine if a date should be disabled in the calendar
  const checkDisableDate = useMemo(
    () => (day: Date) => {
      const dateString = formatInTimeZone(day, timeZone, 'yyyy-MM-dd');
      const dayInEST = formatInTimeZone(day, timeZone, 'yyyy-MM-dd');
      const todayInEST = formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd');
      const isPast = isBefore(new Date(dayInEST), new Date(todayInEST));

      return isPast || !availableDatesSet.has(dateString) || cancelledDatesSet.has(dateString);
    },
    [availableDatesSet, cancelledDatesSet],
  );

  // Handle date selection in the calendar
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      setSelectedDate(null);
      return;
    }
    // Store the date in the target timezone to ensure consistent display
    setSelectedDate(date);
  };

  // Handle selected date changes and update calendar display information
  useEffect(() => {
    if (selectedDate) {
      // Find the event that matches the selected date in the target timezone
      const selectedEvent = allDates.find((event) => {
        // Convert both dates to the target timezone for comparison
        const eventDate = formatInTimeZone(event.date, timeZone, 'yyyy-MM-dd');
        const selected = formatInTimeZone(selectedDate, timeZone, 'yyyy-MM-dd');
        return eventDate === selected;
      });

      if (selectedEvent) {
        const dateString = formatInTimeZone(selectedDate, timeZone, 'yyyy-MM-dd');
        const exception = exceptions?.find((ex) => ex.exceptionDate === dateString);

        // Format the date in EST timezone
        const formattedDate = formatInTimeZone(selectedDate, timeZone, 'MMMM d, yyyy');
        let startDateObj, endDateObj;

        // Create a base date in the target timezone
        const baseDate = formatInTimeZone(selectedDate, timeZone, 'yyyy-MM-dd');

        if (exception?.timeDuration) {
          // Use modified time from exception
          const startTimeStr = exception.timeDuration.startTime;
          const endTimeStr = exception.timeDuration.endTime;

          startDateObj = DateTime.fromISO(`${baseDate}T${startTimeStr}`, {
            zone: timeZone,
          })
            .toUTC()
            .toJSDate();

          endDateObj = DateTime.fromISO(`${baseDate}T${endTimeStr}`, {
            zone: timeZone,
          })
            .toUTC()
            .toJSDate();
        } else {
          // Use regular event time
          const startTimeStr = `${selectedEvent.startTime.getHours().toString().padStart(2, '0')}:${selectedEvent.startTime.getMinutes().toString().padStart(2, '0')}`;
          const endTimeStr = `${selectedEvent.endTime.getHours().toString().padStart(2, '0')}:${selectedEvent.endTime.getMinutes().toString().padStart(2, '0')}`;

          // Convert times to target timezone
          const startDateTime = `${baseDate}T${startTimeStr}`;
          const startDateInTZ = formatInTimeZone(
            new Date(startDateTime),
            timeZone,
            "yyyy-MM-dd'T'HH:mm:ssxxx",
          );
          startDateObj = new Date(startDateInTZ);

          // Create end date in the target timezone
          const endDateTime = `${baseDate}T${endTimeStr}`;
          const endDateInTZ = formatInTimeZone(
            new Date(endDateTime),
            timeZone,
            "yyyy-MM-dd'T'HH:mm:ssxxx",
          );
          endDateObj = new Date(endDateInTZ);
        }

        // Format times for display
        const startTime = formatInTimeZone(startDateObj, timeZone, 'h:mm a');
        const endTime = formatInTimeZone(endDateObj, timeZone, 'h:mm a');

        const startDateTimeInNY = DateTime.fromFormat(
          `${dateString} ${startTime}`,
          'yyyy-MM-dd h:mm a',
          { zone: timeZone },
        ).toJSDate();

        const endDateTimeInNY = DateTime.fromFormat(
          `${dateString} ${endTime}`,
          'yyyy-MM-dd h:mm a',
          { zone: timeZone },
        ).toJSDate();

        const calendarObject = {
          title: `national children's museum - ${title.trim()}`,
          start: startDateTimeInNY,
          end: endDateTimeInNY,
        };

        setCalendarObject(calendarObject);
        setFormattedDate(formattedDate);
        setFormattedTime(`${startTime} - ${endTime} EST`);
      } else {
        setFormattedDate(null);
        setFormattedTime(null);
      }
    }
  }, [selectedDate, allDates, exceptions, timeZone, title]);

  return (
    <div className='mx-auto flex max-w-screen-xl flex-col px-5 py-8 pt-0 md:p-[50px] md:pt-0'>
      <div className='pb-6 last:pb-0 md:pb-9'>
        <Heading title='schedule' />
        {isExpiredEvent ? (
          <p className='rounded bg-pink-500 p-2 text-center text-xl font-medium leading-7 sm:text-lg'>
            This event has expired.
          </p>
        ) : (
          <div>
            <DayPicker
              mode='single'
              timeZone={timeZone}
              disabled={checkDisableDate}
              defaultMonth={availableDates[0] ?? new Date()}
              modifiers={{
                available: availableDates,
                selected: selectedDate ? [new Date(selectedDate)] : [],
              }}
              modifiersClassNames={{
                available: 'border border-green-500 border-2',
                selected: 'bg-green-500',
              }}
              showOutsideDays={false}
              onSelect={handleDateSelect}
              id='calendar-widget'
              className='rounded-lg bg-white p-3'
              classNames={{
                months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 relative',
                month: 'space-y-4 w-full !ml-0',
                month_caption: 'flex justify-center pt-1 relative items-center',
                caption_label: 'text-lg sm:text-xl font-bold leading-7 z-[1]',
                nav: 'space-x-1 flex items-center text-black absolute z-0 w-full justify-between w-full h-8 top-1 z-[1]',
                button_previous: cn(
                  buttonVariants({ variant: 'outline' }),
                  'h-7 w-7 sm:h-8 sm:w-8 bg-transparent p-0 opacity-100 rounded-full border border-white hover:border-black hover:bg-white focus:bg-white active:bg-white active:border-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'absolute left-1 border-0 hover-border-0 before:bg-white before:absolute before:rounded-full before:scale-100 hover:before:scale-[1.15] before:ease-bounce hover:before:ease-bounce before:transition-all hover:before:transition-all hover:before:h-full before:border before:border-black hover:before:w-full before:z-[-1]',
                ),
                button_next: cn(
                  buttonVariants({ variant: 'outline' }),
                  'h-7 w-7 sm:h-8 sm:w-8 bg-transparent p-0 opacity-100 rounded-full border border-white hover:border-black hover:bg-white focus:bg-white active:bg-white active:border-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'absolute right-1 border-0 hover-border-0 before:bg-white before:absolute before:rounded-full before:scale-100 hover:before:scale-[1.15] before:ease-bounce hover:before:ease-bounce before:transition-all hover:before:transition-all hover:before:h-full before:border before:border-black hover:before:w-full before:z-[-1]',
                ),
                month_grid: 'w-full border-collapse space-y-1',
                weekdays: 'flex justify-center',
                weekday:
                  'text-black-500 rounded-full w-[14.28%] text-sm sm:text-base font-medium leading-5 hover:rounded-full',
                week: 'flex w-full mt-2 justify-center',
                day: 'w-[14.28%] aspect-square text-center text-sm sm:text-base p-0 relative border-black rounded-full hover:rounded-full [&:has([aria-selected].day-range-end)]:rounded-full first:[&:has([aria-selected])]:rounded-full last:[&:has([aria-selected])]:rounded-full focus-within:relative focus-within:z-20',
                day_button: cn(
                  buttonVariants({ variant: 'ghost' }),
                  'relative w-full aspect-square z-10 p-0 font-normal text-sm sm:text-base aria-selected:opacity-100 rounded-full [&:not(:disabled)]:before:bg-green-500 [&:not(:disabled)]:before:absolute [&:not(:disabled)]:before:rounded-full [&:not(:disabled)]:before:scale-100 [&:not(:disabled)]:hover:before:scale-[1.15] [&:not(:disabled)]:before:ease-in [&:not(:disabled)]:before:hover:ease-bounce [&:not(:disabled)]:before:transition-all [&:not(:disabled)]:hover:before:h-full [&:not(:disabled)]:hover:before:w-full [&:not(:disabled)]:before:z-[-1]',
                ),
                range_end: 'day-range-end',
                selected:
                  'rounded-full hover:rounded-full bg-green-500 text-sm sm:text-base text-black font-medium hover:bg-green-500 hover:text-black focus:bg-green-500 focus:text-black before:bg-green-500 before:absolute before:rounded-full before:scale-100 hover:before:scale-[1.15] before:ease-bounce hover:before:ease-bounce before:transition-all hover:before:transition-all before:h-full before:w-full before:z-[-1]',
                today:
                  'rounded-full text-sm sm:text-base text-black font-medium hover:rounded-full',
                outside:
                  'day-outside rounded-full text-sm sm:text-base hover:rounded-full text-zinc-400 aria-selected:bg-slate-100/50 aria-selected:text-zinc-400 aria-selected:opacity-30',
                disabled:
                  '[&>button]:text-sm [&>button]:sm:text-base [&>button]:text-zinc-400 [&>button]:border-zinc-200',
                range_middle: 'aria-selected:bg-slate-100 aria-selected:text-slate-900',
                hidden: 'invisible',
              }}
              components={{
                Chevron: (props) => (
                  <img
                    className='h-4'
                    src={
                      props.orientation == 'left'
                        ? '/images/arrow-left-black.svg'
                        : '/images/arrow-right-black.svg'
                    }
                    alt={props.orientation == 'left' ? 'Left arrow' : 'Right arrow'}
                    title=''
                  />
                ),
              }}
            />
            {selectedDate ? (
              <div className='mt-4 flex flex-col items-center justify-center gap-2 rounded-lg bg-white p-4 text-center shadow'>
                {selectedDate && (
                  <div className='flex items-center gap-x-2'>
                    <CalendarIcon className='h-4 w-4' />
                    <div>{formattedDate}</div>
                  </div>
                )}
                {formattedTime && (
                  <div className='flex items-center gap-x-2'>
                    <ClockIcon className='h-4 w-4' />
                    <div>{formattedTime}</div>
                  </div>
                )}
                {calendarObject && <AddToCalendar event={calendarObject} />}
              </div>
            ) : (
              <div className='mt-4 flex flex-col items-center justify-center gap-2 p-4 text-center'>
                Select a date to see the time
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CalendarWidget;
